import { z } from 'zod';
import { BACKUP_METHODS, ONBOARDING_INTENTIONS, DB_TYPES, INSTALL_TYPES, RESTART_MODES, SITE_STATUSES, UPDATE_POLICIES, } from '../constants.js';
import { cronSchema, httpUrlSchema, semVerSchema, uuidSchema } from './common.js';
/**
 * Paramètres d'installation propres à chaque pilote. Stockés chiffrés
 * (sites.install_params_encrypted) et jamais renvoyés tels quels au frontend : seules les
 * valeurs non sensibles sont exposées (cf. sitePublicSchema).
 */
export const installParamsSchema = z.discriminatedUnion('installType', [
    z.object({
        installType: z.literal('ghost_cli_systemd'),
        ghostDir: z.string().min(1),
        systemdUnit: z.string().regex(/^[\w.@-]+$/, 'Nom d’unité systemd invalide'),
        /** Chemin de ghost-cli, quand il n'est pas dans le PATH des sessions non interactives. */
        ghostBin: z.string().min(1).optional(),
    }),
    z.object({
        installType: z.literal('ghost_cli_local'),
        ghostDir: z.string().min(1),
        nodeBin: z.string().min(1).optional(),
        /** Chemin de ghost-cli, quand il n'est pas dans le PATH des sessions non interactives. */
        ghostBin: z.string().min(1).optional(),
    }),
    z.object({
        installType: z.literal('docker_compose'),
        composePath: z.string().min(1),
        serviceName: z.string().min(1).default('ghost'),
        containerName: z.string().min(1).optional(),
        dbServiceName: z.string().min(1).optional(),
    }),
    z.object({
        installType: z.literal('custom'),
        contentPath: z.string().min(1),
        configPaths: z.array(z.string().min(1)).min(1),
        commands: z.object({
            restart: z.string().min(1),
            status: z.string().min(1),
            update: z.string().min(1).optional(),
            rollback: z.string().min(1).optional(),
        }),
    }),
]);
/** Champs de connexion saisis à l'onboarding. Les secrets n'existent qu'en entrée. */
export const siteConnectionInputSchema = z.object({
    name: z.string().trim().min(1).max(120),
    url: httpUrlSchema,
    sshHost: z.string().trim().min(1),
    sshPort: z.number().int().min(1).max(65535).default(22),
    sshUser: z.string().trim().min(1),
    sshPrivateKey: z.string().min(1),
    sshPrivateKeyPassphrase: z.string().optional(),
});
export const siteAdminApiInputSchema = z.object({
    ghostAdminApiUrl: httpUrlSchema,
    /** Clé Admin API Ghost au format `id:secret` (secret hexadécimal). */
    adminApiKey: z
        .string()
        .regex(/^[0-9a-f]{24}:[0-9a-f]{64}$/, 'Format attendu : identifiant:secret (hexadécimal)'),
});
/**
 * Qui redémarre le site, et que faire quand ce n'est pas Vigie.
 *
 * Sur les hébergements où l'application Node.js est supervisée par le fournisseur — un mutualisé
 * Infomaniak, par exemple — elle ne tourne pas sous le compte SSH : `ghost restart` y lancerait un
 * second process qui se disputerait le port, et le seul redémarrage réel est un bouton dans le
 * panneau de l'hébergeur. Le déclarer permet à Vigie d'écrire la configuration puis de dire ce qui
 * reste à faire, au lieu de tenter une commande qui ne peut pas aboutir.
 */
export const restartModeInputSchema = z.object({
    restartMode: z.enum(RESTART_MODES).default('driver'),
    /** Consigne montrée à la personne : propre à l'hébergeur, donc libre. */
    restartHint: z.string().trim().max(300).nullable().default(null),
});
/**
 * L'adresse que Vigie surveille.
 *
 * **Ce réglage ne déplace pas le site.** Il change ce que Vigie sonde et affiche, rien d'autre : ni
 * la configuration de Ghost, ni le fragment de la bordure, ni le certificat. Un site dont on change
 * l'adresse ici sans l'avoir changée sur la machine serait surveillé à une adresse où il ne répond
 * pas — et l'inverse, celui qu'on a basculé sans le dire à Vigie, est le cas qui a motivé ce
 * réglage.
 *
 * Ce dernier s'est produit le 2 octobre 2026 : un site passé sur un nouveau domaine, l'ancien
 * redirigeant en 301. La supervision suivait la redirection, voyait 200, et ne disait rien — il
 * n'existait alors aucun moyen de lui apprendre la nouvelle adresse.
 */
export const adresseSuperviseeInputSchema = z.object({
    url: httpUrlSchema,
});
export const sitePolicyInputSchema = z.object({
    updatePolicy: z.enum(UPDATE_POLICIES).default('manual'),
    backupSchedule: cronSchema.default('30 3 * * *'),
    backupRetentionDays: z.number().int().min(1).max(3650).default(30),
    monitorIntervalMinutes: z.number().int().min(1).max(1440).default(5),
});
/**
 * Représentation d'un site telle qu'exposée par l'API.
 *
 * Aucun champ secret n'y figure : c'est la frontière mécanique qui empêche une clé SSH ou un
 * mot de passe restic d'atteindre le frontend. Toute route qui renvoie un site sérialise
 * via ce schéma.
 */
export const sitePublicSchema = z.object({
    id: uuidSchema,
    organizationId: uuidSchema,
    name: z.string(),
    url: z.string(),
    ghostAdminApiUrl: z.string().nullable(),
    sshHost: z.string(),
    sshPort: z.number().int(),
    sshUser: z.string(),
    sshHostKeyFingerprint: z.string().nullable(),
    installType: z.enum(INSTALL_TYPES),
    dbType: z.enum(DB_TYPES).nullable(),
    nodeVersionDetected: z.string().nullable(),
    ghostVersionDetected: z.string().nullable(),
    updatePolicy: z.enum(UPDATE_POLICIES),
    backupSchedule: z.string(),
    backupRetentionDays: z.number().int(),
    backupMethod: z.enum(BACKUP_METHODS),
    monitorIntervalMinutes: z.number().int(),
    restartMode: z.enum(RESTART_MODES),
    restartHint: z.string().nullable(),
    currentStatus: z.enum(SITE_STATUSES),
    createdAt: z.string().datetime(),
    updatedAt: z.string().datetime(),
});
/** Carte de la vue flotte : le strict nécessaire pour le tableau de bord. */
export const siteFleetCardSchema = sitePublicSchema
    .pick({
    id: true,
    name: true,
    url: true,
    installType: true,
    currentStatus: true,
    ghostVersionDetected: true,
    updatePolicy: true,
})
    .extend({
    diskFreeBytes: z.number().nullable(),
    diskTotalBytes: z.number().nullable(),
    responseTimeMs: z.number().nullable(),
    lastCheckedAt: z.string().datetime().nullable(),
    lastBackupAt: z.string().datetime().nullable(),
    lastBackupStatus: z.string().nullable(),
    latestGhostVersion: semVerSchema.nullable(),
    operationInProgress: z.string().nullable(),
});
// --- Onboarding --------------------------------------------------------------------------------
export const onboardingSshInputSchema = z.object({
    sshHost: z.string().trim().min(1),
    sshPort: z.number().int().min(1).max(65535).default(22),
    sshUser: z.string().trim().min(1),
    /**
     * Mot de passe, pour les hébergements qui n'autorisent pas l'ajout de clé.
     *
     * Absent, la connexion emploie la paire de clés engendrée par l'assistant. Présent, c'est lui qui
     * sert — et la clé n'est alors pas installée sur le serveur, puisqu'on ne peut pas l'y mettre.
     * Certains mutualisés (Infomaniak notamment) ne donnent pas accès à `authorized_keys`.
     */
    sshPassword: z.string().min(1).max(500).optional(),
});
export const onboardingDetectInputSchema = z.object({
    ghostDir: z.string().trim().min(1).optional(),
    composePath: z.string().trim().min(1).optional(),
});
export const onboardingAdminApiInputSchema = siteAdminApiInputSchema;
export const onboardingScriptInputSchema = z.object({
    installType: z.enum(INSTALL_TYPES),
    username: z.string().trim().min(1).max(32).default('vigie'),
    systemdUnit: z.string().trim().min(1).optional(),
    installDir: z.string().trim().min(1).optional(),
    privilegedCommands: z.array(z.string().trim().min(1)).optional(),
});
/** Dernière étape : tout ce qui n'a pas pu être déduit est confirmé ici. */
export const onboardingCompleteInputSchema = z
    .object({
    name: z.string().trim().min(1).max(120),
    url: httpUrlSchema,
    installParams: installParamsSchema,
    dbType: z.enum(DB_TYPES).nullable().default(null),
    ghostVersionDetected: z.string().nullable().default(null),
    nodeVersionDetected: z.string().nullable().default(null),
})
    .merge(sitePolicyInputSchema)
    .merge(restartModeInputSchema);
/**
 * Identité du site, demandée à l'ouverture de l'assistant.
 *
 * Avant la connexion, et non après : la détection peut s'appuyer sur l'URL, le provisionnement en a
 * besoin pour nommer la pile, et l'on évite de saisir deux fois les mêmes champs.
 */
export const onboardingStartInputSchema = z.object({
    name: z.string().trim().min(1).max(120),
    url: httpUrlSchema,
});
/**
 * Ce qu'on est venu faire, décidé **après** la connexion.
 *
 * L'intention ne se demande plus d'emblée : elle se demandait avant que quiconque ait regardé le
 * serveur, donc avant de pouvoir aider à répondre. Elle arrive maintenant une fois la machine
 * sondée, où Vigie peut dire ce qu'elle a trouvé et proposer la réponse la plus probable.
 *
 * Elle reste **un choix**, et non une déduction : un hôte qui héberge déjà un site peut parfaitement
 * en accueillir un second — le test de nuit l'exige — et « Ghost est présent ici » ne dit donc pas
 * lequel des deux gestes on est venu faire.
 *
 * Le serveur la conserve pour refuser un provisionnement sur un site déclaré existant — un clic de
 * trop ne doit pas poser une pile Docker à côté d'un Ghost qui tourne — et pour consigner dans
 * l'audit ce que l'on venait faire.
 */
export const onboardingIntentionInputSchema = z.object({
    intention: z.enum(ONBOARDING_INTENTIONS),
});
//# sourceMappingURL=site.js.map